import { useEffect, useMemo, useState } from "react";

export type OfferKey = "monthly" | "annual_card" | "annual_pix";
export type OfferOption = {
  id: string; productId: string; planId: string; offerKey: OfferKey; offerVersion: number;
  displayName: string; billingModel: "recurring" | "one_time"; paymentMethod: "boleto" | "credit_card" | "pix";
  priceCents: number; installmentCount: number | null; serviceMonths: number; trialEligible: boolean; active: boolean; sortOrder: number;
};
export type CommercialCatalogPlan = {
  planId: string; planName: string; planVersion: number; summary: string; benefits: string[]; activationConditions: string[];
  trialDays: number; trialAiReplyLimit: number; monthlyAiReplyLimit: number; features: Record<string, unknown>; offers: OfferOption[];
};
export type CommercialCatalog = { catalogId: string; releaseId: string; version: number; publishedAt: string; plans: CommercialCatalogPlan[] };
export type CommercialOffer = {
  planId: string; planName: string; version: number; trialDays: number; monthlyPriceCents: number | null;
  trialAiReplyLimit: number; monthlyAiReplyLimit: number;
  offers: Array<Omit<OfferOption, "offerVersion"> & { version: number }>;
};
export type CommercialSelection = { releaseId: string; planId: string; offerKey: OfferKey; planVersion: number; offerVersion: number };

const selectionKey = "g3ai-commercial-selection";
export const qaOnboardingAccessKey = "g3ai-staging-qa-onboarding-access";
const emptyCatalog: CommercialCatalog = { catalogId: "", releaseId: "", version: 0, publishedAt: "", plans: [] };

// A QA pass is carried in the URL fragment, never in a request URL, then kept
// only for the current browser session. The Core still validates the same
// single-use, expiring authorization on registration; this helper merely lets
// staging exercise the public-looking journey without rendering its field.
export function captureQaOnboardingAccess(): boolean {
  if (window.location.hostname !== "staging.g3ai.app") return false;
  const params = new URLSearchParams(window.location.hash.slice(1));
  const token = params.get("qa_onboarding");
  if (!token || !/^[A-Za-z0-9_-]{32,512}$/.test(token)) return false;
  sessionStorage.setItem(qaOnboardingAccessKey, token);
  params.delete("qa_onboarding");
  const remaining = params.toString();
  window.history.replaceState({}, "", `${window.location.pathname}${window.location.search}${remaining ? `#${remaining}` : ""}`);
  return true;
}

export function readQaOnboardingAccess(): string | null {
  if (window.location.hostname !== "staging.g3ai.app") return null;
  const value = sessionStorage.getItem(qaOnboardingAccessKey);
  return value && /^[A-Za-z0-9_-]{32,512}$/.test(value) ? value : null;
}

export function clearQaOnboardingAccess(): void {
  sessionStorage.removeItem(qaOnboardingAccessKey);
}

export function selectedPublishedOffer(catalog: CommercialCatalog, planId: string, offerKey: string): CommercialSelection | null {
  const plan = catalog.plans.find((item) => item.planId === planId);
  const offer = plan?.offers.find((item) => item.offerKey === offerKey && item.active);
  return plan && offer ? { releaseId: catalog.releaseId, planId, offerKey: offer.offerKey, planVersion: plan.planVersion, offerVersion: offer.offerVersion } : null;
}
export function persistCommercialSelection(selection: CommercialSelection): void { sessionStorage.setItem(selectionKey, JSON.stringify(selection)); }
export function readCommercialSelection(): CommercialSelection | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(selectionKey) ?? "null") as Partial<CommercialSelection> | null;
    if (!value || typeof value.releaseId !== "string" || typeof value.planId !== "string" || !["monthly", "annual_card", "annual_pix"].includes(value.offerKey ?? "") || !Number.isInteger(value.planVersion) || !Number.isInteger(value.offerVersion)) return null;
    return value as CommercialSelection;
  } catch { return null; }
}
export function selectionHref(catalog: CommercialCatalog, planId: string, offerKey: OfferKey): string {
  return selectedPublishedOffer(catalog, planId, offerKey) ? `/comecar?plan=${encodeURIComponent(planId)}&offer=${encodeURIComponent(offerKey)}` : "/comecar";
}
export function annualPixSavingCents(plan: CommercialCatalogPlan): number {
  const monthly = plan.offers.find((item) => item.offerKey === "monthly")?.priceCents;
  const annual = plan.offers.find((item) => item.offerKey === "annual_pix")?.priceCents;
  return monthly && annual ? Math.max(0, monthly * 12 - annual) : 0;
}
export function annualPixSavingPercent(plan: CommercialCatalogPlan): number {
  const monthly = plan.offers.find((item) => item.offerKey === "monthly")?.priceCents;
  if (!monthly) return 0;
  return Math.round((annualPixSavingCents(plan) / (monthly * 12)) * 100);
}

export function dailyEquivalentCents(priceCents: number, days: 30 | 365): number {
  if (!Number.isInteger(priceCents) || priceCents < 0) return 0;
  return Math.round(priceCents / days);
}

export function useCommercialCatalog(): { catalog: CommercialCatalog; loading: boolean; unavailable: boolean } {
  const [catalog, setCatalog] = useState(emptyCatalog); const [loading, setLoading] = useState(true); const [unavailable, setUnavailable] = useState(false);
  useEffect(() => {
    let active = true;
    void fetch("/api/v1/public/catalog")
      .then(async (response) => { if (!response.ok) throw new Error("Catálogo indisponível"); return response.json() as Promise<{ data: CommercialCatalog }>; })
      .then(({ data }) => { if (active) setCatalog(data); })
      .catch(() => { if (active) setUnavailable(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);
  return { catalog, loading, unavailable };
}

// Compatibility for approved onboarding and niche pages. They now consume the
// same immutable publication and deliberately have no commercial price fallback.
export function useCommercialOffer(): CommercialOffer {
  const { catalog } = useCommercialCatalog();
  return useMemo(() => {
    const plan = catalog.plans.find((item) => item.planId === "essencial");
    if (!plan) return { planId: "essencial", planName: "G3.AI", version: 0, trialDays: 0, monthlyPriceCents: null, trialAiReplyLimit: 0, monthlyAiReplyLimit: 0, offers: [] };
    return { planId: plan.planId, planName: plan.planName, version: plan.planVersion, trialDays: plan.trialDays,
      monthlyPriceCents: plan.offers.find((item) => item.offerKey === "monthly")?.priceCents ?? null,
      trialAiReplyLimit: plan.trialAiReplyLimit, monthlyAiReplyLimit: plan.monthlyAiReplyLimit,
      offers: plan.offers.map((item) => ({ ...item, version: item.offerVersion })) };
  }, [catalog]);
}
